import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Github, Linkedin, Mail, Download } from 'lucide-react';

const Hero = () => {
  const [text, setText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const [typingSpeed, setTypingSpeed] = useState(150);

  const roles = ['Full Stack Developer', 'UI/UX Designer', 'Problem Solver', 'Tech Enthusiast'];

  const handleDownloadResume = () => {
    const resumeUrl = '/Smarth_Sood_Resume.pdf';
    const fileName = 'Smarth_Sood_Resume.pdf';
    
    // Create a temporary anchor element for download
    const anchor = document.createElement('a');
    anchor.href = resumeUrl;
    anchor.download = fileName;
    anchor.click();
  };

  useEffect(() => {
    const handleType = () => {
      const i = loopNum % roles.length;
      const fullText = roles[i];

      setText(
        isDeleting
          ? fullText.substring(0, text.length - 1)
          : fullText.substring(0, text.length + 1)
      );

      setTypingSpeed(isDeleting ? 30 : 150);

      if (!isDeleting && text === fullText) {
        setTimeout(() => setIsDeleting(true), 500);
      } else if (isDeleting && text === '') {
        setIsDeleting(false);
        setLoopNum(loopNum + 1);
      }
    };

    const timer = setTimeout(handleType, typingSpeed);
    return () => clearTimeout(timer);
  }, [text, isDeleting, loopNum, typingSpeed, roles]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Animated Background with Purple Mesh */}
      <div className="absolute inset-0 mesh-gradient opacity-20"></div>
      
      {/* Floating Purple Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: Math.random() * 6 + 2,
              height: Math.random() * 6 + 2,
              background: `linear-gradient(45deg, #a855f7, #ec4899)`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              x: [0, 100],
              y: [0, 100],
              scale: [1,0.5, 1],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: 100,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        ))}
      </div>

      {/* Glowing Orbs */}
      <div className="absolute top-20 left-20 w-72 h-72 bg-primary-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-orbit-smooth"></div>
      <div className="absolute top-40 right-20 w-72 h-72 bg-accent-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-wave-smooth" style={{ animationDelay: '3s' }}></div>
      <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-secondary-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-orbit-smooth" style={{ animationDelay: '6s' }}></div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10"
      >
        <motion.div
          variants={itemVariants}
          className="mb-8"
        >
          <motion.div className="relative inline-block">
            
           
          </motion.div>
        </motion.div>

        <motion.h1
          variants={itemVariants}
          className="text-5xl md:text-7xl font-bold mb-6"
        >
          Hi, I'm{' '}
          <span className="gradient-text">Smarth Sood</span>
        </motion.h1>

        <motion.div
          variants={itemVariants}
          className="text-2xl md:text-3xl text-gray-600 dark:text-gray-400 mb-8 h-12"
        >
          I'm a <span className="text-primary-500 font-semibold typing-animation">{text}</span>
        </motion.div>

        <motion.p
          variants={itemVariants}
          className="text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed"
        >
          Passionate about creating exceptional digital experiences through clean code,
          innovative design, and  powerful technology. Let's build something amazing together.
        </motion.p>

        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12"
        >
          <motion.button
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="btn-primary px-8 py-4 rounded-full font-semibold text-lg shadow-purple-lg flex items-center gap-2"
            onClick={handleDownloadResume}
          >
            <Download size={20} />
            Download Resume
          </motion.button>
          
          <motion.a
            href="#contact"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-4 border-2 border-primary-500 text-primary-500 rounded-full font-semibold text-lg hover:bg-primary-500 hover:text-white transition-all duration-300 hover:shadow-purple"
          >
            Get In Touch
          </motion.a>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="flex items-center justify-center gap-6"
        >
          {[
            { icon: Github, href: 'https://github.com/SmartSood', label: 'GitHub', color: 'hover:text-gray-900 dark:hover:text-white' },
            { icon: Linkedin, href: 'https://www.linkedin.com/in/smarth-sood-7a4885280/', label: 'LinkedIn', color: 'hover:text-blue-600' },
            { icon: Mail, href: 'mailto:smarthsood@gmail.com', label: 'Email', color: 'hover:text-primary-500' },
          ].map(({ icon: Icon, href, label, color }) => (
            <motion.a
              key={label}
              href={href}
              whileHover={{ scale: 1.2, y: -5 }}
              whileTap={{ scale: 0.9 }}
              className={`p-4 glass rounded-full text-gray-700 dark:text-gray-300 ${color} transition-all duration-300 shadow-lg hover:shadow-purple`}
              aria-label={label}
            >
              <Icon size={24} />
            </motion.a>
          ))}
        </motion.div>

        
      </motion.div>
    </section>
  );
};

export default Hero;